import React, {
    useRef,
    useState,
    useEffect,
    useLayoutEffect,
    ReactNode,
    useCallback,
    AriaRole,
    lazy,
    Suspense,
    useMemo,
    ReactPortal
} from 'react';
import { createPortal } from 'react-dom';
import { createPopper, Placement } from '@popperjs/core';
import { AnimatePresence, Variants } from 'framer-motion';
import { isReactChildrenHasContent } from '../../utils/shared.utils';
import { Popover, Arrow, TargetContainer } from '../HBPopover/HBPopover.styles';
import {
    IOnOpenChange,
    HBPopoverOnOpenChangeTrigger,
    TOpenTrigger,
    HBPopoverTheme
} from './HBPopover.types';
import { TTextAlignment } from '../HBText/HBText.types';
import { HBButtonTheme } from '../HBButton/HBButton.types';
import {
    IActionTestIdProps,
    MustIncludeOneOfBasicTestIdProps
} from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import HBPopoverActions from './HBPopoverActions';
import { useResizeObserver } from '../../hooks/useResizeObserver';

// lazy is needed to avoid circular dependency because the close button has a tooltip that uses the popover
const HBCloseButton = lazy(() => import('../HBCloseButton/HBCloseButton'));

const ARROW_PADDING = 10;

export interface HBPopoverAction extends IActionTestIdProps {
    theme?: HBButtonTheme;
    text: string;
    onClick: (e, setIsOpen) => void;
    disabled?: boolean;
}

/** @deprecated Use HBPopoverAction instead */
export type IAction = HBPopoverAction;

type RenderProps = (
    setIsOpen: ReturnType<typeof usePopover>['setIsOpen'],
    isOpen: boolean
) => ReactNode | ReactNode[];

export interface IPopoverProps {
    /** Action buttons rendered at the bottom of the popover content. */
    actions?: HBPopoverAction[];
    /** Horizontal alignment of the action buttons. Defaults to the container's natural alignment. */
    actionsAlignment?: TTextAlignment;
    /** Accepts ReactNode or a render function receiving (setIsOpen, isOpen) for controlled content. */
    children: ReactNode | ReactNode[] | RenderProps;
    /** CSS class applied to the root popover element. */
    className?: string;
    /** Inline styles applied to the popover panel. Use sparingly; prefer theme and maxWidth props. */
    customStyle?: React.CSSProperties;
    /** Delay in milliseconds before the popover becomes visible after triggering. */
    delayShowInMs?: number;
    /** When true with hover trigger, the popover stays open while the cursor is over the popover content. */
    keepOpenOnHover?: boolean;
    /** Programmatically forces the popover closed, overriding internal open state. */
    forceClose?: boolean;
    /** Programmatically forces the popover open, overriding internal open state. */
    forceOpen?: boolean;
    /** Stops mouseover events from bubbling out of the popover, useful when nested inside a tooltip. */
    shouldNotPropagateMouseOverEvent?: boolean;
    /** With click trigger, keeps the popover open while the user is typing in the target. */
    keepOpenWhileTyping?: boolean;
    /** Horizontal offset in pixels applied to the popover position relative to the target. */
    offsetX?: number;
    /** Vertical offset in pixels applied to the popover position relative to the target. */
    offsetY?: number;
    /** Called when the popover open state changes, with the new state and what triggered it (targetClick, clickOutside, enterKey). */
    onOpenChange?: ({ isOpen, triggeredFrom }: IOnOpenChange) => void;
    /** Determines how the popover opens: 'click' toggles on click, 'hover' on mouseover; omit for always-open behavior. */
    openTrigger?: TOpenTrigger;
    /** Side of the target the popover appears on. Popper.js flips automatically when there is insufficient space. Defaults to 'bottom'. */
    placement?: Placement;
    /** Registers document-level mousedown/touchstart listeners to close the popover when clicking outside. */
    shouldSupportClickOutside?: boolean;
    /** Makes the target container keyboard-focusable by setting tabIndex=0. */
    isTargetContainerFocusable?: boolean;
    /**
     * Accessible name for the target container, applied as `aria-label`. Set this
     * when `target` is a bare icon or other content with no accessible name of its
     * own — otherwise a focusable target container is announced with no name at all.
     *
     * Applies the name only, not an ARIA role: the container has no activation
     * handlers of its own, so `role="button"` would promise an affordance it can't
     * honor. `aria-label` on a roleless element is inconsistently announced across
     * screen readers, so this improves the odds rather than guaranteeing the name.
     */
    targetAriaLabel?: string;
    /** Closes the popover when the Enter key is pressed (does not respond to Escape). */
    shouldSupportCloseOnEnter?: boolean;
    /** Renders the popover in a portal attached to document.body, allowing it to escape overflow:hidden containers. */
    shouldSupportOverflowHidden?: boolean;
    /** When true, stops click events from bubbling when the target is clicked, preventing parent handlers from firing. */
    stopPropagationOnTargetClick?: boolean;
    /** When true, stops click events from bubbling when the popover panel itself is clicked. */
    stopPropagationOnPopoverClick?: boolean;
    /** The trigger element; accepts ReactNode or a render function receiving the current isOpen state. */
    target: ReactNode | Function;
    /** Positions the popover against an element the popover does not render, instead of its own target wrapper. Takes precedence over the target element; pass target={null} when the anchor is fully external. */
    anchorElement?: HTMLElement | null;
    /** CSS class applied to the wrapper element around the target. */
    targetContainerClassName?: string;
    /** Inline styles applied to the wrapper element around the target. */
    targetContainerStyle?: React.CSSProperties;
    /** Visual theme applied to the popover; use 'none' for unstyled. */
    theme?: HBPopoverTheme;
    /** When true, renders a directional arrow pointing from the popover toward the target element. */
    withArrow?: boolean;
    /** When true, renders a close button inside the popover panel. */
    withCloseButton?: boolean;
    /** Opens the popover on the initial render only; does not toggle on subsequent renders. */
    enableInitialOpen?: boolean;
    /** Opens the popover when the target receives keyboard focus (hover trigger mode). */
    openOnFocus?: boolean;
    /** ARIA role applied to the popover panel element (e.g. 'dialog', 'menu', 'tooltip'). */
    role?: AriaRole;
    /** Accessible name for the popover panel, applied as `aria-label`. Pass it together with `role`: a roleless panel is not reliably announced with a name. `targetAriaLabel` names the target container instead. */
    'aria-label'?: string;
    /** Id of the element that names the popover panel, applied as `aria-labelledby`. Pass it together with `role`. It takes precedence over `aria-label` when the id resolves. `targetAriaLabel` names the target container instead. */
    'aria-labelledby'?: string;
    /** Maximum width of the popover panel (any valid CSS value, e.g. '320px'). Use for text-heavy content. */
    maxWidth?: string;
    /** Framer Motion callback fired after the popover exit animation completes. */
    onExitComplete?: () => void;
    /** Custom Framer Motion variants for the popover enter/exit animations, replacing the default opacity fade. */
    variants?: Variants;
    /** Positions the popover at the cursor coordinates on click instead of relative to the target element; requires openTrigger='click'. */
    openOnMousePosition?: boolean;
}

type TPopperConfigOptions = Parameters<typeof createPopper>[2];

const getPopperConfig = (
    placement: Placement,
    withArrow: boolean,
    arrowRef,
    offsetX: number,
    offsetY: number,
    openOnMousePosition: boolean
) => {
    const config: TPopperConfigOptions = { placement, modifiers: [] };

    if (withArrow) {
        config.modifiers.push(
            {
                name: 'arrow',
                options: {
                    element: arrowRef.current
                }
            },
            {
                name: 'offset',
                options: {
                    offset:
                        placement === 'top' || placement === 'bottom'
                            ? [offsetX, offsetY + ARROW_PADDING]
                            : [offsetY, offsetX]
                }
            },
            {
                name: 'hide'
            }
        );
    } else {
        config.modifiers.push({
            name: 'offset',
            options: {
                offset:
                    placement === 'top' || placement === 'bottom'
                        ? [offsetX, offsetY]
                        : [offsetY, offsetX]
            }
        });
    }

    // add preventOverflow modifier for mouse position
    if (openOnMousePosition) {
        config.modifiers.push({
            name: 'preventOverflow',
            options: {
                boundary: 'viewport'
            }
        });
    }

    return config;
};

function usePopover({
    enableInitialOpen,
    openTrigger,
    placement,
    offsetX,
    offsetY,
    withArrow,
    shouldSupportClickOutside,
    shouldSupportCloseOnEnter,
    stopPropagationOnTargetClick,
    keepOpenOnHover,
    onOpenChange,
    forceClose,
    forceOpen,
    keepOpenWhileTyping,
    children,
    openOnFocus,
    openOnMousePosition: openOnMousePositionProp,
    anchorElement,
    isMounted
}: Omit<IPopoverProps, 'target'> & { isMounted?: boolean }) {
    const popoverRef = useRef(null);
    const arrowRef = useRef(null);
    const targetRef = useRef(null);
    const popoverInstance = useRef(null);
    const [targetIsFocus, setTargetIsFocus] = useState(false);
    const virtualElementRef = useRef(null);

    const openOnMousePosition =
        openOnMousePositionProp && openTrigger === 'click';

    const shouldOpen = !forceClose && isReactChildrenHasContent(children);
    // We assume here that if the user doesn't send any openTrigger
    // it means that he wants the popover to be always open.
    // In case we want to set the initial open state of the popover we can use the prop 'enableInitialOpen'.
    // This prop will set the popover open only for the initial render.
    const [_isOpen, _setIsOpen] = useState(
        enableInitialOpen || (shouldOpen && !openTrigger)
    );
    const isOpen = shouldOpen && (_isOpen || forceOpen);

    // onOpenChange should be triggered whenever _setIsOpen is called
    const setIsOpen = useCallback(
        ({
            newIsOpen,
            triggeredFrom
        }: {
            newIsOpen: boolean;
            triggeredFrom?: HBPopoverOnOpenChangeTrigger;
        }) => {
            _setIsOpen(newIsOpen);
            onOpenChange?.({ isOpen: newIsOpen, triggeredFrom });
        },
        [onOpenChange]
    );

    const handleDocumentClick = useCallback(
        e => {
            if (
                !targetRef.current?.contains(e.target) &&
                !anchorElement?.contains(e.target) &&
                !popoverRef.current?.contains(e.target)
            ) {
                setIsOpen({
                    newIsOpen: false,
                    triggeredFrom: HBPopoverOnOpenChangeTrigger.ClickOutside
                });
            }
        },
        [setIsOpen, targetRef, popoverRef, anchorElement]
    );

    const handleKeypress = useCallback(
        e => {
            if (e.key === 'Enter') {
                setIsOpen({
                    newIsOpen: false,
                    triggeredFrom: HBPopoverOnOpenChangeTrigger.EnterKey
                });
            }
        },
        [setIsOpen]
    );

    // virtual element for mouse position when openOnMousePosition is true
    const createVirtualElement = useCallback((x: number, y: number) => {
        return {
            getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                top: y,
                right: x,
                bottom: y,
                left: x
            })
        };
    }, []);

    // create popper
    useLayoutEffect(() => {
        const referenceElement = openOnMousePosition
            ? virtualElementRef.current
            : anchorElement ?? targetRef.current;
        if (popoverRef?.current && referenceElement && isOpen) {
            popoverInstance.current = createPopper(
                referenceElement,
                popoverRef.current,
                getPopperConfig(
                    placement,
                    withArrow,
                    arrowRef,
                    offsetX,
                    offsetY,
                    openOnMousePosition
                )
            );
        }
    }, [
        placement,
        withArrow,
        isOpen,
        offsetX,
        offsetY,
        openOnMousePosition,
        anchorElement,
        isMounted
    ]);

    const updatePopover = useCallback(() => {
        popoverInstance.current?.update();
    }, []);

    // update popover position when popover sizes change, i.e. when images load
    useResizeObserver(popoverRef, updatePopover);

    useEffect(() => {
        updatePopover();
    }, [children, updatePopover]);

    // support click outside
    const currentPopoverRef = popoverRef.current;
    useEffect(() => {
        const events: (keyof DocumentEventMap)[] = ['mousedown', 'touchstart'];
        if (isOpen && shouldSupportClickOutside) {
            events.forEach(event => {
                document.addEventListener(event, handleDocumentClick, true);
            });
            return () => {
                events.forEach(event => {
                    document.removeEventListener(
                        event,
                        handleDocumentClick,
                        true
                    );
                });
            };
        }
    }, [
        currentPopoverRef,
        handleDocumentClick,
        isOpen,
        shouldSupportClickOutside
    ]);

    // support close on enter
    useEffect(() => {
        if (isOpen && shouldSupportCloseOnEnter) {
            document.addEventListener('keypress', handleKeypress);
            return () => {
                document.removeEventListener('keypress', handleKeypress);
            };
        }
    }, [handleKeypress, isOpen, shouldSupportCloseOnEnter]);

    // Init isOpen to false when shouldOpen becomes false
    useEffect(() => {
        if (!shouldOpen) {
            setIsOpen({ newIsOpen: false });
        }
    }, [shouldOpen, setIsOpen]);

    const triggerConfig: any = {};
    const popoverTriggerConfig: any = {};

    if (openTrigger === 'click') {
        triggerConfig.onClick = ev => {
            if (stopPropagationOnTargetClick) {
                ev.stopPropagation();
            }

            if (openOnMousePosition) {
                virtualElementRef.current = createVirtualElement(
                    ev.clientX,
                    ev.clientY
                );
            }

            setIsOpen({
                newIsOpen: !isOpen,
                triggeredFrom: HBPopoverOnOpenChangeTrigger.TargetClick
            });
        };
        if (keepOpenWhileTyping) {
            triggerConfig.onKeyDown = () => {
                if (!isOpen) {
                    setIsOpen({ newIsOpen: true });
                }
            };
        }
    } else if (openTrigger === 'hover') {
        triggerConfig.onMouseOver = () => {
            if (!isOpen) {
                setIsOpen({ newIsOpen: true });
            }
        };
        triggerConfig.onMouseLeave = () => {
            if (!openOnFocus || !targetIsFocus) {
                setIsOpen({ newIsOpen: false });
            }
        };

        if (openOnFocus) {
            triggerConfig.onFocus = () => {
                setTargetIsFocus(true);
                if (!isOpen) {
                    setIsOpen({ newIsOpen: true });
                }
            };
            triggerConfig.onBlur = () => {
                setTargetIsFocus(false);
                setIsOpen({ newIsOpen: false });
            };
        }

        if (keepOpenOnHover) {
            popoverTriggerConfig.onMouseOver = () => {
                setIsOpen({ newIsOpen: true });
            };

            popoverTriggerConfig.onMouseLeave = () => {
                setIsOpen({ newIsOpen: false });
            };
        }
    }

    return {
        isOpen,
        setIsOpen,
        targetRef,
        popoverRef,
        arrowRef,
        triggerConfig,
        popoverTriggerConfig
    };
}

const HBPopover = ({
    enableInitialOpen,
    testIdName,
    testId,
    actions,
    actionsAlignment,
    children,
    className,
    customStyle,
    delayShowInMs,
    keepOpenOnHover = true,
    forceClose = false,
    forceOpen = false,
    shouldNotPropagateMouseOverEvent = false,
    keepOpenWhileTyping,
    offsetX = 0,
    offsetY = 0,
    onOpenChange = () => {},
    openTrigger,
    placement = 'bottom',
    shouldSupportClickOutside,
    shouldSupportCloseOnEnter,
    shouldSupportOverflowHidden,
    isTargetContainerFocusable,
    targetAriaLabel,
    stopPropagationOnTargetClick,
    stopPropagationOnPopoverClick,
    target,
    targetContainerClassName,
    targetContainerStyle = {},
    theme = 'popover',
    withArrow,
    withCloseButton = false,
    openOnFocus,
    onExitComplete,
    role,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    maxWidth,
    variants,
    openOnMousePosition,
    anchorElement
}: IPopoverProps & MustIncludeOneOfBasicTestIdProps) => {
    const [safeCreatePortal, isPortalReady] = useCreatePortal();

    const {
        isOpen,
        triggerConfig,
        popoverTriggerConfig,
        targetRef,
        popoverRef,
        arrowRef,
        setIsOpen
    } = usePopover({
        enableInitialOpen,
        openTrigger,
        placement,
        offsetX,
        offsetY,
        withArrow,
        shouldSupportClickOutside,
        shouldSupportCloseOnEnter,
        keepOpenOnHover,
        stopPropagationOnTargetClick,
        stopPropagationOnPopoverClick,
        onOpenChange,
        forceClose,
        forceOpen,
        keepOpenWhileTyping,
        children,
        openOnFocus,
        openOnMousePosition,
        anchorElement,
        isMounted: shouldSupportOverflowHidden ? isPortalReady : true
    });

    const transformedChildren =
        typeof children === 'function' ? children(setIsOpen, isOpen) : children;

    const transformedTarget =
        typeof target === 'function' ? target(isOpen) : target;

    const showingOpacity = customStyle?.opacity ? customStyle.opacity : 1;

    const stopPropagatingMouseOver = (
        // this is useful when the popover is wrapped with a tooltip,
        // hovering over the popover content will fire the onmouseover, so you should want to stop it.
        ev: React.SyntheticEvent<HTMLDivElement>
    ) => {
        ev.stopPropagation();
    };

    const onClick = (ev: React.SyntheticEvent<HTMLDivElement>) => {
        if (stopPropagationOnPopoverClick) {
            ev.stopPropagation();
        }
    };

    const onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
        if (stopPropagationOnPopoverClick) {
            ev.stopPropagation();
        }
    };

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'popover',
        componentName: 'HBPopover'
    });

    const defaultVariants = useMemo(
        () => ({
            hidden: {
                opacity: 0,
                transition: {
                    duration: 0.2
                }
            },
            visible: {
                opacity: showingOpacity,
                transition: {
                    duration: 0.2,
                    delay: (delayShowInMs || 0) / 1000
                }
            }
        }),
        [delayShowInMs, showingOpacity]
    );

    const finalVariants = variants || defaultVariants;

    const popover = (
        <AnimatePresence onExitComplete={onExitComplete}>
            {isOpen && (
                <Popover
                    className={className}
                    ref={popoverRef}
                    onMouseOver={
                        shouldNotPropagateMouseOverEvent
                            ? stopPropagatingMouseOver
                            : undefined
                    }
                    onClick={onClick}
                    onKeyDown={onKeyDown}
                    theme={theme}
                    style={customStyle}
                    $isOpen={isOpen}
                    {...popoverTriggerConfig}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={finalVariants}
                    role={role}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    // Clicking plain content would otherwise move focus to <body>, and a
                    // popover whose target is hidden only stays visible while it holds focus.
                    tabIndex={withArrow ? -1 : undefined}
                    $offsetX={offsetX}
                    $offsetY={offsetY}
                    $keepOpenOnHover={keepOpenOnHover}
                    $maxWidth={maxWidth}
                >
                    {transformedChildren}
                    {actions && (
                        <HBPopoverActions
                            actionsAlignment={actionsAlignment}
                            actions={actions}
                            clickCallback={setIsOpen}
                            marginTop={16}
                        />
                    )}
                    {withArrow && (
                        <Arrow ref={arrowRef} data-popper-arrow theme={theme} />
                    )}
                    {withCloseButton && (
                        <Suspense fallback={<></>}>
                            <HBCloseButton
                                distance={8}
                                size="xs"
                                theme="dark"
                                testIdName={finalTestId}
                                onClick={() => setIsOpen({ newIsOpen: false })}
                            />
                        </Suspense>
                    )}
                </Popover>
            )}
        </AnimatePresence>
    );

    const portalOrPopover = shouldSupportOverflowHidden
        ? safeCreatePortal(popover)
        : popover;

    return (
        <>
            <TargetContainer
                data-popover-state={isOpen ? 'open' : 'closed'}
                ref={targetRef}
                {...triggerConfig}
                style={targetContainerStyle}
                className={targetContainerClassName}
                tabIndex={isTargetContainerFocusable ? 0 : undefined}
                // Deliberately no `role="button"` alongside the name: the
                // container has no click/keydown activation of its own (the
                // hover trigger's config carries only mouse/focus handlers), so
                // announcing it as a button would promise an affordance it
                // can't honor. A real `<button>` trigger is tracked separately.
                aria-label={targetAriaLabel}
                data-testid={finalTestId ? `${finalTestId}-button` : undefined}
            >
                {transformedTarget}
            </TargetContainer>

            {portalOrPopover}
        </>
    );
};

export default HBPopover;

type PortalCreator = (
    component: JSX.Element
) => JSX.Element | ReactPortal | null;

// createPortal cannot be run in SSR, so we need to provide a fallback on the server (null).
// In addition, the hook can only return createPortal AFTER the first hook cycle on client-side,
// otherwise the first rendered DOM will mismatch the server-rendered one, which results in a
// rehydration error and a full re-render of the page
function useCreatePortal(): [PortalCreator, boolean] {
    const [isPortalReady, setIsPortalReady] = React.useState(false);

    useLayoutEffect(() => {
        if (typeof document !== 'undefined' && document.body) {
            setIsPortalReady(true);
        }
    }, []);

    const createFn = isPortalReady
        ? (jsx: JSX.Element) => createPortal(jsx, document.body)
        : () => null;

    return [createFn, isPortalReady];
}
